import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, Mail, ArrowUpRight } from "lucide-react";
import { CLINIC, NAV_LINKS } from "../data/clinic";
import { FadeUp, MaskedLine } from "./Reveal";
import logo from "../assets/optiplus-logo.png";

const Footer = () => (
    <footer className="relative overflow-hidden border-t border-emerald-500/15 bg-[#0b150e]">
        <div className="noise-overlay pointer-events-none absolute inset-0 opacity-25" />
        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-8 lg:px-16 lg:py-28">
            <FadeUp className="mb-20 text-center lg:mb-28">
                <p className="mb-6 text-xs font-mono tracking-[0.3em] uppercase text-emerald-400">
                    Your vision deserves precision
                </p>
                <h2 className="font-display text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl lg:text-7xl">
                    <MaskedLine mode="inView">See the world</MaskedLine>
                    <MaskedLine mode="inView" delay={0.12}>
                        <em className="text-emerald-400">clearly</em> again.
                    </MaskedLine>
                </h2>
                <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                    <a
                        href={CLINIC.phoneHref}
                        data-testid="footer-cta-call"
                        className="group flex items-center gap-3 rounded-full bg-emerald-500 px-8 py-4 text-sm font-semibold text-[#04120a] transition-all duration-300 hover:bg-emerald-400 hover:shadow-[0_0_36px_rgba(34,197,94,0.45)]"
                    >
                        <Phone className="h-4 w-4" />
                        Call to Book · {CLINIC.phoneDisplay}
                    </a>
                    <Link
                        to="/remedy"
                        data-testid="footer-cta-remedy"
                        className="group flex items-center gap-2 rounded-full border border-emerald-500/30 px-8 py-4 text-sm font-medium text-emerald-200 transition-all duration-300 hover:border-emerald-400/70 hover:bg-emerald-500/10"
                    >
                        Explore Our Remedies
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                </div>
            </FadeUp>

            <div className="grid gap-12 border-t border-emerald-500/10 pt-14 md:grid-cols-2 lg:grid-cols-4">
                <div>
                    <img src={logo} alt="Optiplus" className="mb-5 h-9 w-auto brightness-[1.35]" />
                    <p className="max-w-xs text-sm font-light leading-relaxed text-[#a3b8a8]">
                        {CLINIC.tagline} in {CLINIC.addressShort} — high-quality, patient-centered
                        eye care for all ages.
                    </p>
                </div>
                <div>
                    <p className="mb-5 text-xs font-mono tracking-[0.25em] uppercase text-emerald-400">Explore</p>
                    <ul className="space-y-3">
                        {NAV_LINKS.map((l) => (
                            <li key={l.to}>
                                <Link
                                    to={l.to}
                                    className="text-sm text-[#a3b8a8] transition-colors duration-300 hover:text-emerald-300"
                                >
                                    {l.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
                <div>
                    <p className="mb-5 text-xs font-mono tracking-[0.25em] uppercase text-emerald-400">Visit</p>
                    <div className="space-y-4 text-sm font-light text-[#a3b8a8]">
                        <p className="flex gap-3">
                            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                            {CLINIC.address}
                        </p>
                        <a
                            href={CLINIC.phoneHref}
                            data-testid="footer-phone-link"
                            className="flex items-center gap-3 transition-colors duration-300 hover:text-emerald-300"
                        >
                            <Phone className="h-4 w-4 shrink-0 text-emerald-500" />
                            {CLINIC.phoneDisplay}
                        </a>
                        <a
                            href={CLINIC.phoneHref2}
                            data-testid="footer-phone-link-landline"
                            className="flex items-center gap-3 pl-7 transition-colors duration-300 hover:text-emerald-300"
                        >
                            {CLINIC.phoneDisplay2}
                        </a>
                        <a
                            href={CLINIC.emailHref}
                            data-testid="footer-email-link"
                            className="flex items-center gap-3 transition-colors duration-300 hover:text-emerald-300"
                        >
                            <Mail className="h-4 w-4 shrink-0 text-emerald-500" />
                            {CLINIC.email}
                        </a>
                    </div>
                </div>
                <div>
                    <p className="mb-5 text-xs font-mono tracking-[0.25em] uppercase text-emerald-400">Hours</p>
                    <div className="space-y-3 text-sm font-light text-[#a3b8a8]">
                        <p className="flex items-center gap-3">
                            <Clock className="h-4 w-4 shrink-0 text-emerald-500" />
                            {CLINIC.hours}
                        </p>
                        <p className="pl-7 text-[#687f6e]">{CLINIC.sunday}</p>
                    </div>
                </div>
            </div>

            <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-emerald-500/10 pt-8 text-xs text-[#687f6e] sm:flex-row">
                <p>© {new Date().getFullYear()} Optiplus · Naktala, Kolkata</p>
                <a
                    href={CLINIC.googleProfile}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="footer-google-link"
                    className="font-mono tracking-[0.2em] uppercase transition-colors duration-300 hover:text-emerald-300"
                >
                    ★ {CLINIC.rating} on Google ↗
                </a>
            </div>
        </div>
    </footer>
);

export default Footer;
